import Store from '@ember-data/store';
import Controller from '@ember/controller';
import { action } from '@ember/object';
import Router from '@ember/routing/router';
import { scheduleOnce } from '@ember/runloop';
import { service } from '@ember/service';
import { isEmpty, isPresent } from '@ember/utils';
import { cached, tracked } from '@glimmer/tracking';
import type { BulkActionsContactsChangedArgs } from 'donor-elf-web/components/contact/bulk-actions';
import { FormListSelectionOption } from 'donor-elf-web/components/form/list-selection';
import { sortByProperties } from 'donor-elf-web/lib/array-utils';
import {
  type AppliedPropertyFilter,
  type Operation,
  deserializeAppliedFilters,
  serializeAppliedFilters,
} from 'donor-elf-web/lib/filter-utils-new';
import type { BulkActionType } from 'donor-elf-web/models/bulk-action';
import CampaignModel from 'donor-elf-web/models/campaign';
import ContactModel, {
  type ContactCustomProperty,
} from 'donor-elf-web/models/contact';
import JournalModel from 'donor-elf-web/models/journal';
import NoteModel from 'donor-elf-web/models/note';
import AbilitiesService from 'donor-elf-web/services/abilities';
import AddSomethingModal, {
  type ContactAddedArgs,
  type TaskAddedArgs,
} from 'donor-elf-web/services/add-something-modal';
import CampaignService from 'donor-elf-web/services/campaign-service';
import ContactsCache, {
  type ContactPropertyFilterInfo,
} from 'donor-elf-web/services/contacts-cache';
import ContactsExportService from 'donor-elf-web/services/contacts-export';
import FormatDateService from 'donor-elf-web/services/format-date-service';
import FundsService from 'donor-elf-web/services/funds-service';
import JournalService from 'donor-elf-web/services/journal-service';
import PowerMenuService from 'donor-elf-web/services/power-menu';
import FileSaver from 'file-saver';
import Papa from 'papaparse';

class ContactTableColumn {
  constructor(props = {}) {
    Object.assign(this, props);
    this.isContactColumn = this.isContactColumn || false;
    this.isJournalColumn = this.isJournalColumn || false;
    this.isCustomPropertyColumn = this.isCustomPropertyColumn || false;
  }
  // Ember Table property
  name!: string;
  isFixed?: boolean;
  width?: number;

  // custom properties
  isContactColumn!: boolean;
  isJournalColumn!: boolean;
  isCampaignColumn!: boolean;
  isCustomPropertyColumn!: boolean;
  propertyName!: string;
  filterPropertyName?: string;
  // true when the contacts can be grouped by this property
  canGroupByProperty?: boolean;
  canEditPropertyDefinition?: boolean;
  @tracked highlightColumn?: boolean;
}

let PropertyNamesThatArentCustom = {
  address: true,
  phones: true,
  emails: true,
  groups: true,
} as any;

export class ContactTableCellState {
  constructor(args: { propertyName: string }) {
    this.propertyName = args.propertyName;
    this.isCustomProperty =
      PropertyNamesThatArentCustom[args.propertyName] == null;
  }

  propertyName: string;
  isCustomProperty: boolean;
  editing = false;

  changeset?: any;
  showValidationErrors = false;
  saving = false;
  removing = false;
  saveError?: any;
  // used to show the property value while it's saving
  tempCustomProperty?: ContactCustomProperty;

  groupOptions?: FormListSelectionOption[];
  selectedGroupOptions?: FormListSelectionOption[];
}

export class ContactTableRow {
  constructor(props: {
    contact: ContactModel;
    selected?: boolean;
    customPropertyNames?: string[];
    journals?: JournalModel[];
    campaigns?: CampaignModel[];
  }) {
    this.contact = props.contact;
    this.journals = props.journals;
    this.campaigns = props.campaigns;
    this.selected = props.selected || false;
    let propertyNames = [
      ...Object.keys(PropertyNamesThatArentCustom),
      ...(props.customPropertyNames || []),
    ];
    for (let propertyName of propertyNames) {
      this.cellStates[propertyName] = new ContactTableCellState({
        propertyName,
      });
    }
  }

  contact!: ContactModel;
  journals?: JournalModel[];
  campaigns?: CampaignModel[];
  @tracked selected!: boolean;
  // only used for mobile table
  @tracked expanded: boolean = false;

  @tracked cellStates: { [propertyName: string]: ContactTableCellState } = {};

  resetCellState(cellState: ContactTableCellState) {
    this.cellStates[cellState.propertyName] = new ContactTableCellState({
      propertyName: cellState.propertyName,
    });
    this.fireCellStatesChanged();
  }

  // call this whenever the cellState is changed so the template knows about it
  fireCellStatesChanged() {
    this.cellStates = { ...this.cellStates };
  }
}

type ContactTablePropertyFilterArgs = {
  operation?: Operation;
  val: any;
  filteredTableRows: ContactTableRow[];
};

interface ContactsTablePropertyFilterInfo extends ContactPropertyFilterInfo {
  filterContactTableRows?: (
    args: ContactTablePropertyFilterArgs,
  ) => ContactTableRow[];
}

export default class ContactsIndexController extends Controller {
  @service declare abilities: AbilitiesService;
  @service declare addSomethingModal: AddSomethingModal;
  @service declare campaignService: CampaignService;
  @service declare contactsCache: ContactsCache;
  @service declare contactsExport: ContactsExportService;
  @service declare formatDateService: FormatDateService;
  @service declare fundsService: FundsService;
  @service declare journalService: JournalService;
  @service declare media: any;
  @service declare powerMenu: PowerMenuService;
  @service declare store: Store;
  @service declare router: Router;

  queryParams = [
    'filters',
    'filtersInitiallyOpened',
    'firstColumnPropertyName',
  ];
  @tracked filters?: string;
  @tracked filtersInitiallyOpened?: boolean;
  @tracked firstColumnPropertyName?: string;
  @tracked appliedFilters?: AppliedPropertyFilter[];
  @tracked applyingFilters = false;
  @tracked contactNameFilterVal?: string;
  @tracked highlightedIndex = 0;
  @tracked downloadingNotes = false;
  @tracked performBulkActionType?: BulkActionType;

  @tracked tableColumns: ContactTableColumn[] = [];
  @tracked tableRows: ContactTableRow[] = [];
  @tracked filteredTableRows: ContactTableRow[] = [];
  lastFilteredTableRowSelectedIndex?: number;
  addSomethingContactAddedToken?: string;
  addSomethingTaskAddedToken?: string;
  campaignContactsChangedToken?: string;
  controllerIsActive = false;

  get perPage() {
    return this.media.isSmallDown ? 25 : 50;
  }

  get filterApplied() {
    return isPresent(this.appliedFilters);
  }

  get filteredContactIds() {
    return this.filteredTableRows?.map((x) => x.contact.id) || [];
  }

  get someContactsAreSelected() {
    return this.selectedContactIds.length > 0;
  }

  get selectedContactIds() {
    return this.tableRows.filter((x) => x.selected).map((x) => x.contact.id);
  }

  get allFilteredTableRowsSelected() {
    return (
      this.filteredTableRows.length > 0 &&
      this.filteredTableRows.every((x) => x.selected)
    );
  }

  @action
  adjustTableContainerHeight(el: HTMLElement) {
    let newHeight =
      window.innerHeight -
      (el.getBoundingClientRect().top + window.scrollY) -
      20;
    el.style.height = `${newHeight}px`;
  }

  get mobileTableColumns() {
    return [new ContactTableColumn({ name: 'Name' })];
  }

  setTableColumns() {
    let tableColumns: ContactTableColumn[] = [];
    tableColumns.push(
      new ContactTableColumn({
        name: 'Name',
        isFixed: 'left',
        width: 225,
        propertyName: 'name',
        isContactColumn: true,
      }),
    );
    tableColumns.push(
      new ContactTableColumn({
        name: 'Address',
        isFixed: true,
        width: 200,
        propertyName: 'address',
      }),
    );
    tableColumns.push(
      new ContactTableColumn({
        name: 'Phone',
        isFixed: true,
        width: 135,
        propertyName: 'phones',
        filterPropertyName: 'contactPhones',
      }),
    );
    tableColumns.push(
      new ContactTableColumn({
        name: 'Email',
        isFixed: true,
        width: 200,
        propertyName: 'emails',
        filterPropertyName: 'contactEmails',
      }),
    );
    tableColumns.push(
      new ContactTableColumn({
        name: 'Groups',
        isFixed: true,
        width: 250,
        propertyName: 'groups',
        filterPropertyName: 'contactGroups',
        canGroupByProperty: true,
      }),
    );

    if (this.journalService.journals.length > 0) {
      tableColumns.push(
        new ContactTableColumn({
          name: 'Journals',
          isFixed: true,
          width: 250,
          propertyName: 'journals',
          isJournalColumn: true,
        }),
      );
    }
    if (
      this.campaignService.campaignsEnabled &&
      this.campaignService.sortedCampaigns.length > 0
    ) {
      tableColumns.push(
        new ContactTableColumn({
          name: 'Campaigns',
          isFixed: true,
          width: 250,
          propertyName: 'campaigns',
          isCampaignColumn: true,
        }),
      );
    }

    for (let customProperty of this.contactsCache.customProperties) {
      let canGroupByProperty =
        customProperty.t === 'l' || customProperty.t === 'b';
      tableColumns.push(
        new ContactTableColumn({
          name: customProperty.l,
          isFixed: true,
          width: 200,
          isCustomPropertyColumn: true,
          propertyName: customProperty.l,
          filterPropertyName: `contact_custom_${customProperty.l}`,
          canGroupByProperty,
          canEditPropertyDefinition: true,
        }),
      );
    }
    if (this.firstColumnPropertyName != null) {
      let propertyColumn = tableColumns.find(
        (x) => x.propertyName === this.firstColumnPropertyName,
      );
      if (propertyColumn) {
        tableColumns = [
          propertyColumn,
          ...tableColumns.filter((x) => x !== propertyColumn),
        ];
      } else if (this.firstColumnPropertyName === 'Groups') {
        propertyColumn = tableColumns.find((x) => x.propertyName === 'groups')!;
        tableColumns = [
          propertyColumn,
          ...tableColumns.filter((x) => x !== propertyColumn),
        ];
      }
    }
    this.tableColumns = tableColumns;
  }

  highlightTableColumns() {
    let filteredPropertyNames =
      this.appliedFilters?.map((x) => x.propertyName) || [];
    for (let tableColumn of this.tableColumns) {
      tableColumn.highlightColumn =
        tableColumn.filterPropertyName != null &&
        filteredPropertyNames.includes(tableColumn.filterPropertyName);
    }
  }

  setTableRows() {
    let customPropertyNames = this.contactsCache.customProperties.map(
      (x) => x.l,
    );
    let { sortedJournalInfos } = this.journalService;
    let { campaignsEnabled, sortedCampaigns } = this.campaignService;

    this.tableRows = sortByProperties(
      this.contactsCache.contacts.map((contact) => {
        let journals = undefined;
        if (sortedJournalInfos.length > 0) {
          let contactJournalInfos =
            this.journalService.journalInfosByContactId[contact.id] || [];
          journals = sortedJournalInfos
            .filter((j) => contactJournalInfos.includes(j))
            .map((j) => j.journal);
        }

        let campaigns = undefined;
        if (campaignsEnabled && sortedCampaigns.length > 0) {
          let campaignContacts =
            this.campaignService.campaignContactsByContactId[contact.id] || [];
          campaigns = sortedCampaigns.filter((c) =>
            campaignContacts.some((cc) => cc.campaignId === c.id),
          );
        }

        return new ContactTableRow({
          contact,
          customPropertyNames,
          journals,
          campaigns,
        });
      }),
      ['contact.name:asc'],
    );
  }

  @action
  toggleRowSelected(row: ContactTableRow, e: any) {
    let rowIndex = this.filteredTableRows.indexOf(row);
    let selected = !row.selected;
    if (e.shiftKey) {
      let startIndex = Math.min(
        rowIndex,
        this.lastFilteredTableRowSelectedIndex || 0,
      );
      let endIndex = Math.max(
        rowIndex,
        this.lastFilteredTableRowSelectedIndex || 0,
      );
      for (let i = startIndex; i <= endIndex; i++) {
        this.filteredTableRows[i].selected = selected;
      }
    } else {
      row.selected = selected;
    }
    this.lastFilteredTableRowSelectedIndex = rowIndex;
  }

  @action
  toggleRowExpanded(row: ContactTableRow) {
    row.expanded = !row.expanded;
  }

  @action
  toggleAllFilteredSelected(el: any) {
    if (el.target.checked) {
      let { filteredContactIds } = this;
      for (let row of this.tableRows) {
        if (filteredContactIds.includes(row.contact.id)) {
          if (!row.selected) {
            row.selected = true;
          }
        } else {
          if (row.selected) {
            row.selected = false;
          }
        }
      }
    } else {
      for (let row of this.tableRows) {
        if (row.selected) {
          row.selected = false;
        }
      }
    }
  }

  @action
  clearSelectedRows() {
    for (let row of this.tableRows) {
      if (row.selected) {
        row.selected = false;
      }
    }
  }

  @action
  handleGroupByProperty(label: string) {
    this.router.transitionTo('funds.fund.contacts.group-by.property', label);
  }

  @action
  handleEditPropertyDefinition(label: string) {
    this.router.transitionTo('funds.fund.properties.property.index', label);
  }

  get actionMenuItems() {
    let menuItems: Array<{
      label: string;
      svgJar: string;
      route?: string;
      onClick?: () => void;
    }> = [
      {
        label: 'Group By Property',
        svgJar: 'layer-group',
        route: 'funds.fund.contacts.group-by',
      },
    ];

    if (this.abilities.hasAbility('edit_contact')) {
      menuItems.push({
        label: 'Import Contacts',
        svgJar: 'plus',
        route: 'funds.fund.contacts.upload',
      });
    }

    menuItems.push({
      label: 'Download Notes',
      svgJar: 'download',
      onClick: this.downloadNotes,
    });

    menuItems.push({
      label: 'Download',
      svgJar: 'download',
      onClick: this.download,
    });

    return menuItems;
  }

  @action
  templateDidInsert() {
    this.controllerIsActive = true;

    if (this.campaignService.campaignsEnabled) {
      if (this.campaignContactsChangedToken == null) {
        // don't need to unsubscribe because the controller is a singleton object
        this.campaignContactsChangedToken =
          this.campaignService.subscribeToCampaignContactsChanged(() => {
            if (this.controllerIsActive) {
              this.setTableRows();
              this.applyTheFilters();
            }
          });
      }

      // intentionally not awaiting this because the page will still work while it's fetching
      this.campaignService.ensureAllCampaignContactsLoaded();
    }

    this.setTableColumns();
    this.setTableRows();
    this.powerMenu.prependOptionsFn('contacts-index', this.powerMenuOptions);
    this.contactsCache.nextPreviousContactIds = this.filteredTableRows.map(
      (x) => x.contact.id,
    );
    this.highlightedIndex = 0;
    this.addSomethingContactAddedToken =
      this.addSomethingModal.subscribeToContactAdded(
        this.handleAddSomethingContactAdded,
      );
    this.addSomethingTaskAddedToken =
      this.addSomethingModal.subscribeToTaskAdded(
        this.handleAddSomethingTaskAdded,
      );
    this.deserializeFilters();
    scheduleOnce('afterRender', this, function () {
      if (this.filtersInitiallyOpened) {
        // remove this query param after the initial render
        this.filtersInitiallyOpened = undefined;
      }
    });
  }

  @action
  templateWillDestroy() {
    this.controllerIsActive = false;
    this.downloadingNotes = false;
    this.filters = undefined;
    this.firstColumnPropertyName = undefined;
    this.applyingFilters = false;
    this.performBulkActionType = undefined;
    this.lastFilteredTableRowSelectedIndex = undefined;
    if (this.addSomethingContactAddedToken) {
      this.addSomethingModal.unsubscribeWithToken(
        this.addSomethingContactAddedToken,
      );
    }
    if (this.addSomethingTaskAddedToken) {
      this.addSomethingModal.unsubscribeWithToken(
        this.addSomethingTaskAddedToken,
      );
    }
    this.powerMenu.removeOptions('contacts-index');
  }

  @action
  powerMenuOptions() {
    return [
      {
        label: 'Download Contacts',
        fn: this.download,
      },
      {
        label: 'Contacts by Group',
        searchBy: 'Group',
        route: 'funds.fund.groups',
      },
    ];
  }

  @cached
  get availablePropertyFilters(): ContactsTablePropertyFilterInfo[] {
    let contactsTableFilters: ContactsTablePropertyFilterInfo[] = [
      {
        propertyName: 'selected',
        label: 'Row Selected',
        propertyType: 'no-operation',
        filterContactTableRows: (
          args: ContactTablePropertyFilterArgs,
        ): ContactTableRow[] => {
          return args.filteredTableRows.filter((x) => x.selected);
        },
        custom: {
          filter: (): ContactModel[] => {
            // this won't be called
            return [];
          },
          includeOnAnyReport: false,
        },
      },
      {
        propertyName: 'not-selected',
        label: 'Row Not Selected',
        propertyType: 'no-operation',
        filterContactTableRows: (
          args: ContactTablePropertyFilterArgs,
        ): ContactTableRow[] => {
          return args.filteredTableRows.filter((x) => !x.selected);
        },
        custom: {
          filter: (): ContactModel[] => {
            // this won't be called
            return [];
          },
          includeOnAnyReport: false,
        },
      },
    ];

    const allJournals = this.journalService.journals;

    if (allJournals.length > 0) {
      contactsTableFilters.push({
        propertyName: 'journalIds',
        label: 'Journal',
        propertyType: 'list',
        listOptions: this.journalService.journals.map((x) => ({
          val: x.id,
          label: x.label,
        })),
        filterContactTableRows: (
          args: ContactTablePropertyFilterArgs,
        ): ContactTableRow[] => {
          const journalIds = args.val as string[];
          const selectedJournals = () =>
            allJournals.filter((j) => journalIds.includes(j.id));

          switch (args.operation) {
            case 'any': // in any
              return args.filteredTableRows.filter((r) =>
                selectedJournals().some((j) =>
                  j.contactIds.includes(r.contact.id),
                ),
              );
            case 'all': // in all
              return args.filteredTableRows.filter((r) =>
                selectedJournals().every((j) =>
                  j.contactIds.includes(r.contact.id),
                ),
              );
            case 'none': // not in
              return args.filteredTableRows.filter(
                (r) =>
                  !selectedJournals().some((j) =>
                    j.contactIds.includes(r.contact.id),
                  ),
              );
            case 'present': // is present
              return args.filteredTableRows.filter((r) =>
                allJournals.some((j) => j.contactIds.includes(r.contact.id)),
              );
            case 'absent': // is absent
              return args.filteredTableRows.filter(
                (r) =>
                  !allJournals.some((j) => j.contactIds.includes(r.contact.id)),
              );
            default:
              return args.filteredTableRows;
          }
        },
        custom: {
          filter: (): ContactModel[] => {
            // this won't be called
            return [];
          },
          includeOnAnyReport: false,
        },
      });
    }

    let filtersFromContactsCache =
      this.contactsCache.availablePropertyFiltersForContacts;
    let hiddenFilter = filtersFromContactsCache.find(
      (x) => x.propertyName === 'contact_show_hidden',
    )!;
    return [
      ...filtersFromContactsCache.filter((x) => x !== hiddenFilter),
      ...contactsTableFilters,
      hiddenFilter,
    ];
  }

  get contactsCacheAppliedFilters() {
    return (
      this.appliedFilters?.filter(
        (x) =>
          this.availablePropertyFilters.find(
            (y) => y.propertyName === x.propertyName,
          )!.filterContactTableRows == null,
      ) || []
    );
  }

  get contactsTableAppliedFilters() {
    return (
      this.appliedFilters?.filter(
        (x) =>
          this.availablePropertyFilters.find(
            (y) => y.propertyName === x.propertyName,
          )!.filterContactTableRows != null,
      ) || []
    );
  }

  async applyTheFilters() {
    this.applyingFilters = true;
    this.highlightedIndex = 0;
    let filteredTableRows = this.tableRows.slice();
    let { contactsCacheAppliedFilters, contactsTableAppliedFilters } = this;
    for (let contactsTableAppliedFilter of contactsTableAppliedFilters) {
      let filterInfo = this.availablePropertyFilters.find(
        (x) => x.propertyName === contactsTableAppliedFilter.propertyName,
      )!;
      filteredTableRows = filterInfo.filterContactTableRows!({
        operation: contactsTableAppliedFilter.operation,
        val: contactsTableAppliedFilter.val,
        filteredTableRows,
      });
    }
    let contactsCacheFilteredContactIds = (
      await this.contactsCache.filter(contactsCacheAppliedFilters, {
        excludeHiddenUnlessFiltered: true,
      })
    ).map((x) => x.id);
    this.filteredTableRows = filteredTableRows.filter((x) =>
      contactsCacheFilteredContactIds.includes(x.contact.id),
    );
    this.contactsCache.nextPreviousContactIds = this.filteredTableRows.map(
      (x) => x.contact.id,
    );

    let contactNameFilter: AppliedPropertyFilter | undefined =
      this.appliedFilters?.find((x) => x.propertyName === 'contactNames');
    if (contactNameFilter) {
      this.contactNameFilterVal = contactNameFilter.val as string;
    } else if (isPresent(this.contactNameFilterVal)) {
      this.contactNameFilterVal = undefined;
    }
    this.highlightTableColumns();

    this.applyingFilters = false;
  }

  @action
  handleFiltersChanged(currentFilters: AppliedPropertyFilter[]) {
    this.appliedFilters = currentFilters;
    this.serializeFilters();
    this.applyTheFilters();
    let hilightedColumn = this.tableColumns.find((x) => x.highlightColumn);
    if (hilightedColumn) {
      hilightedColumn.highlightColumn = false;
    }
  }

  @action
  clearFilters() {
    this.handleFiltersChanged([]);
  }

  serializeFilters() {
    this.filters = serializeAppliedFilters(this.appliedFilters);
  }

  deserializeFilters() {
    this.appliedFilters = deserializeAppliedFilters(this.filters);
    this.applyTheFilters();
  }

  @action
  handleSearchContactNames(e: any) {
    let text = e.target.value as string | undefined;
    if (isEmpty(text) || text!.length < 3) {
      text = undefined;
    }
    this.contactNameFilterVal = e.target.value;

    if (text) {
      let contactNameFilter: AppliedPropertyFilter | undefined =
        this.appliedFilters?.find((x) => x.propertyName === 'contactNames');
      if (contactNameFilter == null) {
        contactNameFilter = {
          propertyName: 'contactNames',
        };
        this.appliedFilters = [
          ...(this.appliedFilters || []),
          contactNameFilter,
        ];
      }
      contactNameFilter.operation = 'stringContains';
      contactNameFilter.val = text;
      this.serializeFilters();
      this.applyTheFilters();
    } else {
      let contactNameFilter: AppliedPropertyFilter | undefined =
        this.appliedFilters?.find((x) => x.propertyName === 'contactNames');
      if (contactNameFilter) {
        this.appliedFilters = this.appliedFilters!.filter(
          (x) => x !== contactNameFilter,
        );
        this.serializeFilters();
        this.applyTheFilters();
      }
    }
  }

  @action
  async handleAddSomethingContactAdded(_msg: string, _data: ContactAddedArgs) {
    this.setTableRows();
    this.applyTheFilters();
  }

  handleAddSomethingTaskAdded = async (_: string, data: TaskAddedArgs) => {
    if (data.task.journalStageId) {
      const contactIds =
        data.task.contactIds ||
        (data.task.contactId ? [data.task.contactId] : []);

      for (const contactId of contactIds) {
        if (this.journalService.journalInfosByContactId[contactId] == null) {
          await this.journalService.load({ forceReload: true });

          this.setTableRows();
          this.applyTheFilters();

          break;
        }
      }
    }
  };

  @action
  handleBulkActionClicked(actionType: BulkActionType, dropdown: any) {
    this.performBulkActionType = actionType;
    dropdown.actions.close();
  }

  @action
  handleBulkActionAddTask(contactIds: string[]) {
    this.performBulkActionType = undefined;
    this.addSomethingModal.open({
      type: 'task',
      taskOptions: {
        contactIds,
      },
    });
  }

  @action
  handleBulkActionsOnContactsChanged(args: BulkActionsContactsChangedArgs) {
    if (args.newPropertyAdded) {
      this.setTableColumns();
      this.setTableRows();
    } else if (args.bulkActionType === 'delete_contacts') {
      this.setTableRows();
    }
    this.applyTheFilters();
  }

  @action
  handleBulkActionsOnClose() {
    this.performBulkActionType = undefined;
  }

  @action
  async downloadNotes() {
    this.downloadingNotes = true;
    let notes = (await this.store.query('note', {})).slice();
    let notesByContactId: { [contactId: string]: NoteModel[] } = {};
    for (let note of notes) {
      if (notesByContactId[note.contactId] == null) {
        notesByContactId[note.contactId] = [];
      }
      notesByContactId[note.contactId].push(note);
    }

    let rows = [];
    let contactsToDownload = this.filteredTableRows.map((x) => x.contact);
    if (this.someContactsAreSelected) {
      let { selectedContactIds } = this;
      contactsToDownload = this.tableRows
        .filter((x) => selectedContactIds.includes(x.contact.id))
        .map((x) => x.contact);
    }
    let headerRow = ['Contact Name', 'Note', 'Date', ''];
    let headerInfo = await this.contactsExport.addContactsHeaderColumns(
      headerRow,
      contactsToDownload,
    );
    rows.push(headerRow);
    for (let contact of contactsToDownload) {
      for (let note of notesByContactId[contact.id] || []) {
        let row = [
          contact.name,
          note.text,
          this.formatDateService.formatForFile(note.createdAt),
          '',
        ];
        await this.contactsExport.addContactInfoToRow(row, contact, headerInfo);
        rows.push(row);
      }
    }

    let csv = Papa.unparse(rows);
    var blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    FileSaver.saveAs(blob, 'contact-notes.csv', true);

    this.downloadingNotes = false;
  }

  @action
  async download() {
    let contactsToDownload = this.filteredTableRows.map((x) => x.contact);
    if (this.someContactsAreSelected) {
      let { selectedContactIds } = this;
      contactsToDownload = this.tableRows
        .filter((x) => selectedContactIds.includes(x.contact.id))
        .map((x) => x.contact);
    }
    let rows = await this.contactsExport.perform(contactsToDownload);

    // Added this for Jeff Mashaw
    if (this.fundsService.selectedFund!.id === '52699') {
      let rowIndex = 0;
      rows[rowIndex].insertAt(0, 'Contact Id');
      rowIndex += 1;
      for (let contact of contactsToDownload) {
        rows[rowIndex].insertAt(0, contact.id);
        rowIndex += 1;
      }
    }

    this.store.createRecord('contact-download').save();

    let csv = Papa.unparse(rows);
    var blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    FileSaver.saveAs(blob, 'contacts.csv', true);
  }
}
